import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  checkCoverage,
  checkCoverageKey,
  conditionKey,
  findingKindKey,
  questionKey,
  targetBasisKey,
  type CheckFinding,
  type CheckProgress,
  type Mandate,
  type MandateInput,
  type NegotiationPreview,
} from '@entities/negotiation';
import { money } from '@shared/lib/money';
import { percentFromBps } from '@shared/lib/percent';
import { Button, Checkbox, TextField } from '@shared/ui';

import styles from './BargainingSetup.module.css';

/** Условия вознаграждения с сервера (`/config.success_fee`). */
export interface FeeTerms {
  rate_bps: number;
  flat_cents: number;
}

interface BargainingSetupProps {
  preview: NegotiationPreview;
  currency: string;
  /** Открытые пункты проверки — их пользователь видит перед ценой (§21.5). */
  findings: CheckFinding[];
  /**
   * Ход проверки по автомобилю; `null` — переписки ещё нет. Нужен на случай,
   * когда открытых пунктов нет: пустой список значит «ответил на всё» только
   * при завершённой проверке, а до неё — что спрашивать ещё не начинали или не
   * закончили (FE-584).
   */
  checkProgress: CheckProgress | null;
  /** Сколько своих вопросов разрешено. Число с сервера. */
  maxQuestions: number;
  busy: boolean;
  /** Ошибки по полям из `422 MANDATE_INVALID`: код → сообщение. */
  fieldErrors: Record<string, string>;
  /**
   * `select` — выбор автомобиля и первый мандат: три вопроса и подтверждение
   * с условиями вознаграждения. `counter` — встречное предложение после ответа
   * автохауса: только максимум и подтверждение, условия и вопросы берутся из
   * действующего мандата (§22.7).
   */
  mode: 'select' | 'counter';
  /**
   * Писать автохаусу нельзя, и вызывающий экран **уже назвал причину**
   * (объявление снято, адреса нет, продавец в списке отказов). Опросник её не
   * повторяет и не выводит сам: предпосылки **новой** переписки знает экран, а
   * не форма. На карточке уже идущей переписки этого гейта нет вовсе — там
   * тред существует по определению, и `can_start` предпросмотра говорит о
   * другом вопросе (CLAUDE.md §21.2).
   */
  blocked?: boolean;
  /** Действующий мандат — в режиме `counter`. */
  initial?: Mandate | null;
  /** Условия вознаграждения; `null` — контур выключен, согласия не спрашивают. */
  fee: FeeTerms | null;
  onSubmit: (mandate: MandateInput) => void;
  onCancel?: (() => void) | undefined;
}

type Step = 'conditions' | 'price' | 'questions' | 'confirm';

const SELECT_STEPS: readonly Step[] = ['conditions', 'price', 'questions', 'confirm'];
const COUNTER_STEPS: readonly Step[] = ['price', 'confirm'];

/**
 * Значение **поля ввода**, а не текст на экране: «27455.00» без разделителя
 * тысяч, чтобы его можно было править и разобрать обратно (`cents`). Суммы в
 * тексте — через общий `money` в локали интерфейса (FE-579).
 */
function inputAmount(cents: number): string {
  return (cents / 100).toFixed(2);
}

function cents(value: string): number {
  const parsed = Number.parseFloat(value.replace(',', '.'));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

/**
 * Опросник перед торгом (FE-460, MVP_PRD §21.3, §21.5; решение заказчика от
 * 12.09.2026).
 *
 * Вопросы появляются **по одному**, сверху карточки переписки, и после
 * подтверждения опросник исчезает — на его месте встаёт «Karl написал
 * автохаусу». Три вопроса, и ни один не косметический:
 *
 * * **какие условия были бы интересны** — Karl попробует договориться о них,
 *   но автохаусу они называются приглашением, а не требованием. Обязательных
 *   условий здесь нет: одна цена и одно «согласен / не согласен»;
 * * **максимальная цена** — рядом стоят открытые пункты проверки: на что
 *   автохаус не ответил, от чего отказался, где разошёлся. Это не диагноз, а
 *   то, что Karl назовёт основанием цены. Число уходит автохаусу первым и
 *   как максимум: **запаса за ним нет**, целевая и предельная цена мандата
 *   равны, и встречный ответ автохауса всегда возвращается к пользователю;
 * * **свои вопросы** — уходят в том же письме, дословно.
 *
 * Режима автономности, окна отмены, срока мандата и передачи почты в опроснике
 * нет: письмо уходит сразу, значения по умолчанию задаёт backend. Кнопка
 * неактивна, пока не отмечены поручение и — при первом мандате — условия
 * вознаграждения: молчаливого согласия в вопросе о деньгах не бывает.
 */
export function BargainingSetup({
  preview,
  currency,
  findings,
  checkProgress,
  maxQuestions,
  busy,
  fieldErrors,
  mode,
  blocked = false,
  initial = null,
  fee,
  onSubmit,
  onCancel,
}: BargainingSetupProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const headingId = useId();

  const steps = mode === 'counter' ? COUNTER_STEPS : SELECT_STEPS;
  const [stepIndex, setStepIndex] = useState(0);
  const step = steps[stepIndex] ?? 'confirm';

  const [conditions, setConditions] = useState<string[]>(() => initial?.preferred_conditions ?? []);
  const [price, setPrice] = useState(() =>
    inputAmount(initial?.target_price.amount_cents ?? preview.target_price.amount_cents),
  );
  const questionSlots = Math.max(0, maxQuestions);
  const [questions, setQuestions] = useState<string[]>(() => {
    const known = initial?.custom_questions.slice(0, questionSlots) ?? [];
    return [...known, ...Array<string>(Math.max(0, questionSlots - known.length)).fill('')];
  });
  // Согласие даётся на **каждую** версию заново: прежняя подпись относится к
  // прежним границам.
  const [accepted, setAccepted] = useState(false);
  // Согласие с условиями вознаграждения — один раз на заказ, при первом
  // мандате (§42.2); встречное предложение его не спрашивает.
  const feeTerms = mode === 'select' ? fee : null;
  const feeRequired = feeTerms !== null;
  const [feeAccepted, setFeeAccepted] = useState(false);

  const priceCents = cents(price);
  const priceValid =
    priceCents >= preview.minimum_target_price_cents &&
    priceCents <= preview.listing_price.amount_cents;
  const askedQuestions = questions.map((item) => item.trim()).filter((item) => item.length > 0);
  const listing = preview.listing_price.amount_cents;
  const exampleDiscount = Math.max(0, listing - priceCents);
  const exampleFee =
    feeTerms === null ? 0 : Math.round((exampleDiscount * feeTerms.rate_bps) / 10_000);

  const submit = () => {
    onSubmit({
      // Максимум покупателя уходит обеими границами: запаса за ним нет (§21.3).
      target_price_cents: priceCents,
      walk_away_price_cents: priceCents,
      required_conditions: [],
      preferred_conditions: conditions,
      custom_questions: mode === 'counter' ? (initial?.custom_questions ?? []) : askedQuestions,
      excluded_question_codes: [],
      purchase_timeline: 'month',
      autonomy_mode: 'autonomous',
      // Окна отмены в опроснике нет: письмо уходит сразу, как письма проверки.
      // Ноль — из списка сервера, а не литерал.
      hold_window_minutes: preview.hold_window_choices.includes(0)
        ? 0
        : (preview.hold_window_choices[0] ?? 0),
      share_user_email: false,
      valid_for_days: preview.valid_days_choices.includes(30)
        ? 30
        : (preview.valid_days_choices[0] ?? 30),
      consent: { text_hash: preview.mandate_consent_text_hash, accepted: true },
    });
  };

  const next = () => {
    setStepIndex((index) => Math.min(index + 1, steps.length - 1));
  };
  const back = () => {
    setStepIndex((index) => Math.max(index - 1, 0));
  };

  return (
    <section className={styles.wrap} aria-labelledby={headingId}>
      <p className={styles.progress}>
        {t('negotiation.setup.step', { current: stepIndex + 1, total: steps.length })}
      </p>
      <h2 id={headingId} className={styles.title}>
        {t(`negotiation.setup.${mode}.title`)}
      </h2>

      {step === 'conditions' ? (
        <fieldset className={styles.group}>
          <legend className={styles.question}>{t('negotiation.setup.conditions.question')}</legend>
          <p className={styles.note}>{t('negotiation.setup.conditions.hint')}</p>
          <ul className={styles.conditions}>
            {preview.condition_codes.map((code) => (
              <li key={code}>
                <Checkbox
                  checked={conditions.includes(code)}
                  onChange={() => {
                    setConditions((list) =>
                      list.includes(code) ? list.filter((item) => item !== code) : [...list, code],
                    );
                  }}
                >
                  {t(conditionKey(code))}
                </Checkbox>
              </li>
            ))}
          </ul>
        </fieldset>
      ) : null}

      {step === 'price' ? (
        <fieldset className={styles.group}>
          <legend className={styles.question}>{t('negotiation.setup.price.question')}</legend>
          {/* Открытые пункты проверки стоят **перед** полем цены: пользователь
              называет сумму, зная, на что Karl сошлётся. Это не диагноз —
              вопросы без ответа, отказы и расхождения (§2). */}
          {/* Пустой список — не «автохаус ответил на всё»: фраза следует из хода
              проверки (FE-584, «спросить ≠ проверить»). */}
          {findings.length === 0 ? (
            <p className={styles.note}>{t(checkCoverageKey(checkCoverage(checkProgress)))}</p>
          ) : (
            <>
              <p className={styles.note}>{t('negotiation.setup.price.findingsLead')}</p>
              <ul className={styles.findings}>
                {findings.map((finding) => (
                  <li key={finding.code} className={styles.finding}>
                    <span className={styles.findingHead}>
                      {t(questionKey(finding.code))}
                      {' — '}
                      {t(findingKindKey(finding.kind))}
                    </span>
                    {finding.quote === null ? null : (
                      <blockquote className={styles.quote}>{finding.quote}</blockquote>
                    )}
                  </li>
                ))}
              </ul>
              <p className={styles.caption}>{t('negotiation.setup.price.findingsNote')}</p>
            </>
          )}

          <TextField
            label={t('negotiation.setup.price.label', { currency })}
            inputMode="decimal"
            value={price}
            error={
              fieldErrors.target_price_cents ??
              fieldErrors.walk_away_price_cents ??
              (priceValid
                ? undefined
                : t('negotiation.mandate.priceBounds', {
                    minimum: money(preview.minimum_target_price_cents, currency, locale, 'auto'),
                    listing: money(listing, currency, locale, 'auto'),
                  }))
            }
            hint={t(targetBasisKey(preview.target_price.basis), {
              median:
                preview.median_price_cents === null
                  ? ''
                  : money(preview.median_price_cents, currency, locale, 'auto'),
              listing: money(listing, currency, locale, 'auto'),
            })}
            onChange={(event) => {
              setPrice(event.target.value);
            }}
          />
          <p className={styles.note}>{t('negotiation.setup.price.maximumNote')}</p>
        </fieldset>
      ) : null}

      {step === 'questions' ? (
        <fieldset className={styles.group}>
          <legend className={styles.question}>{t('negotiation.setup.questions.question')}</legend>
          <p className={styles.note}>
            {t('negotiation.setup.questions.hint', { count: questionSlots })}
          </p>
          {questions.map((question, index) => (
            <TextField
              // Полей ровно `maxQuestions` и их порядок фиксирован — индекс стабилен.
              key={index}
              label={t('negotiation.mandate.question', { index: index + 1 })}
              value={question}
              maxLength={200}
              error={index === 0 ? fieldErrors.custom_questions : undefined}
              onChange={(event) => {
                const value = event.target.value;
                setQuestions((list) => list.map((item, at) => (at === index ? value : item)));
              }}
            />
          ))}
        </fieldset>
      ) : null}

      {step === 'confirm' ? (
        <div className={styles.group}>
          <p className={styles.question}>{t(`negotiation.setup.${mode}.confirmQuestion`)}</p>
          <dl className={styles.summary}>
            <div>
              <dt>{t('negotiation.setup.summary.price')}</dt>
              <dd className="tabular">{money(priceCents, currency, locale, 'auto')}</dd>
            </div>
            <div>
              <dt>{t('negotiation.setup.summary.conditions')}</dt>
              <dd>
                {conditions.length === 0
                  ? t('negotiation.setup.summary.none')
                  : conditions.map((code) => t(conditionKey(code))).join(', ')}
              </dd>
            </div>
            {mode === 'select' ? (
              <div>
                <dt>{t('negotiation.setup.summary.questions')}</dt>
                <dd>
                  {askedQuestions.length === 0
                    ? t('negotiation.setup.summary.none')
                    : askedQuestions.join(' · ')}
                </dd>
              </div>
            ) : null}
          </dl>

          {mode === 'select' ? (
            // Выбор окончателен (§34.4): сказано до нажатия, а не после.
            <p className={styles.warning} role="note">
              {t('negotiation.mandate.finalChoice')}
            </p>
          ) : null}

          <Checkbox
            checked={accepted}
            onChange={(event) => {
              setAccepted(event.target.checked);
            }}
          >
            {t('negotiation.mandate.consent')}
          </Checkbox>
          {fieldErrors.consent === undefined ? null : (
            <p className={styles.error} role="alert">
              {fieldErrors.consent}
            </p>
          )}

          {feeTerms !== null ? (
            // Условия вознаграждения с примером на числах **этого** автомобиля
            // (§42.2): абстрактные «25 % от скидки» ничего не говорят.
            <div className={styles.fee}>
              <p className={styles.note}>
                {t('negotiation.mandate.feeTerms', {
                  rate: percentFromBps(feeTerms.rate_bps, locale),
                  flat: money(feeTerms.flat_cents, currency, locale, 'auto'),
                })}
              </p>
              <p className={styles.caption}>
                {t('negotiation.mandate.feeExample', {
                  listing: money(listing, currency, locale, 'auto'),
                  target: money(priceCents, currency, locale, 'auto'),
                  discount: money(exampleDiscount, currency, locale, 'auto'),
                  fee: money(exampleFee, currency, locale, 'auto'),
                })}
              </p>
              <Checkbox
                checked={feeAccepted}
                onChange={(event) => {
                  setFeeAccepted(event.target.checked);
                }}
              >
                {t('negotiation.mandate.feeConsent')}
              </Checkbox>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={styles.actions}>
        {stepIndex === 0 ? (
          onCancel === undefined ? null : (
            <Button type="button" variant="ghost" disabled={busy} onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          )
        ) : (
          <Button type="button" variant="secondary" disabled={busy} onClick={back}>
            {t('negotiation.setup.back')}
          </Button>
        )}
        {step === 'confirm' ? (
          <Button
            type="button"
            disabled={busy || !accepted || (feeRequired && !feeAccepted) || !priceValid || blocked}
            onClick={submit}
          >
            {busy ? t('negotiation.mandate.pending') : t(`negotiation.setup.${mode}.submit`)}
          </Button>
        ) : (
          // «Weiter» — шаг опросника, а не действие: красной на экране
          // остаётся одна отправка в конце (DESIGN_SYSTEM §5.1). Синий контур
          // отмечает движение вперёд (§2.3).
          <Button
            type="button"
            variant="outline-action"
            disabled={busy || (step === 'price' && !priceValid)}
            onClick={next}
          >
            {t('negotiation.setup.next')}
          </Button>
        )}
      </div>
      {step === 'confirm' && (!accepted || (feeRequired && !feeAccepted)) ? (
        <p className={styles.caption}>{t('negotiation.mandate.consentRequired')}</p>
      ) : null}
    </section>
  );
}
